import type { PfStatusSummary } from '@pa/api/clinic'
import type { ReactNode } from 'react'
import { heartbeatAge } from '../model/heartbeat'
import { isAwaitingCode } from '../model/labels'
import { fmtDuration } from '../model/uptime'
import { minutesSince } from '../model/format'

/** What the card knows about the open incident when the clinic is down (from #29: its title and start). */
export interface OpenIncidentInfo {
  title: string
  startedAt: string
}

export interface PfStatusCardProps {
  pf: PfStatusSummary | undefined
  /** The open incident behind a "down" status (the card's line 2), once loaded. */
  incident?: OpenIncidentInfo | null | undefined
  now: number
  /** Wraps the card (the clinic app: a link to C-12). Default: a plain `div`. */
  wrap?: (className: string, children: ReactNode) => ReactNode
}

type Dot = '' | 'warn' | 'down' | 'idle'

function lines(
  pf: PfStatusSummary,
  incident: OpenIncidentInfo | null | undefined,
  now: number,
): { dot: Dot; title: string; detail: ReactNode; heartbeat: boolean } {
  const down = incident ? ` · down ${fmtDuration(minutesSince(incident.startedAt, now))}` : ''
  switch (pf.status) {
    case 'healthy':
      return {
        dot: '',
        title: 'Practice Fusion connected',
        detail: (
          <>
            Session <b>logged in</b> · keeper <b>healthy</b>
          </>
        ),
        heartbeat: true,
      }
    case 'degraded':
      // Task 25: PF Keeper is on PF's security code page; it enters the code when the SMS arrives.
      if (isAwaitingCode(pf)) {
        return {
          dot: 'warn',
          title: 'PF waiting for a security code',
          detail: 'PF Keeper enters it when the text message arrives',
          heartbeat: true,
        }
      }
      return {
        dot: 'warn',
        title: 'Practice Fusion connection unstable',
        detail: pf.degradedReason ?? 'Checking PF Keeper…',
        heartbeat: true,
      }
    case 'down':
      return pf.pfKeeperApp === 'down'
        ? {
            dot: 'down',
            title: 'PF Keeper not responding',
            detail: `Online booking unavailable${down}`,
            heartbeat: true,
          }
        : {
            dot: 'down',
            title: 'Practice Fusion logged out',
            detail: `${incident?.title ?? 'Practice Fusion session down'}${down}`,
            heartbeat: true,
          }
    case 'unknown':
      return { dot: 'idle', title: 'No data', detail: 'No health check in the last few minutes', heartbeat: true }
    case 'not_connected':
      return { dot: 'idle', title: 'Not connected', detail: 'PF Keeper not connected', heartbeat: false }
    case 'inactive':
      return { dot: 'idle', title: 'Inactive', detail: 'This clinic is inactive', heartbeat: false }
  }
}

/**
 * The clinic sidebar's PF status card (UserFlows §4.2, Architecture P-05; Observability §5.6's lines): healthy,
 * degraded, down (app), down (session), no data, not connected. "Last heartbeat Ns ago" counts up from `now`.
 */
export function PfStatusCard({ pf, incident, now, wrap }: PfStatusCardProps) {
  if (!pf) return <div className="bridge" aria-busy="true" />
  const l = lines(pf, incident, now)
  const body = (
    <>
      <div className="bridge-row">
        <span className={`bridge-dot ${l.dot}`} aria-hidden /> {l.title}
      </div>
      <div className="bridge-meta">
        <span>{l.detail}</span>
        {l.heartbeat && (
          <span>
            Last heartbeat{' '}
            <b>
              {pf.lastHeartbeatAt === null ? 'more than 2 days ago' : `${heartbeatAge(pf.lastHeartbeatAt, now)} ago`}
            </b>
          </span>
        )}
      </div>
    </>
  )
  const className = `bridge pf-${pf.status}`
  return wrap ? wrap(`${className} clickable`, body) : <div className={className}>{body}</div>
}
